import React from 'react'
import { cn } from '@/utilities/ui'
import type { TableCell, CommitmentTerm } from '../types'
import { getCellContent } from '../utils'

interface HeaderCellProps {
  cell: TableCell
  cellIndex: number
  isHighlighted: boolean
  isLastColumn: boolean
  isLeftOfHighlighted: boolean
  isComparison: boolean
  displayPeriod: 'hourly' | 'monthly'
  displayCommitmentTerm: CommitmentTerm
}

export const HeaderCell: React.FC<HeaderCellProps> = ({
  cell,
  cellIndex,
  isHighlighted,
  isLastColumn,
  isLeftOfHighlighted,
  isComparison,
  displayPeriod,
  displayCommitmentTerm,
}) => {
  const { primary, secondary } = getCellContent(cell, displayPeriod, displayCommitmentTerm)

  const cellClasses = cn(
    'text-center align-middle',
    // Minimum width for mobile
    'min-w-40',
    // Bottom border
    'border-b border-civo-cloud/50',
    // Right border on all columns except the last, highlighted, or left of highlighted
    !isLastColumn && !isHighlighted && !isLeftOfHighlighted && 'border-r border-r-civo-cloud/50',
    // Desktop styles
    'p-7',
    // Tablet/Mobile styles
    'max-xl:p-7',
    // Highlighted column styles - top of column has rounded corners and shadow
    isHighlighted &&
      'bg-white border-t border-x border-civo-cloud rounded-t-[20px] shadow-[0px_2px_8px_rgba(0,0,0,0.08)]',
  )

  const renderContent = () => {
    if (cell.variant === 'blank') {
      return <span className="sr-only">Empty cell</span>
    }

    // Comparison tables use body text styles for all header cells
    if (isComparison) {
      return (
        <div className="flex flex-col items-center justify-center gap-1 break-words">
          <span className="body-small xl:body-copy-medium text-black break-words">
            {primary}
          </span>
          {secondary && (
            <span className="body-xsmall italic text-black/50 break-words">
              {secondary}
            </span>
          )}
        </div>
      )
    }

    return (
      <span className="table-header text-black break-words">
        {primary}
      </span>
    )
  }

  return (
    <th key={cell.id || cellIndex} scope="col" className={cellClasses}>
      {renderContent()}
    </th>
  )
}
